import type {
  Asked,
  Bookmarked,
  BookmarkView,
  ChatFileUpload,
  ChatFileView,
  ChangesPage,
  ChannelDirectoryPage,
  ChannelVisibility,
  ConversationRead,
  ConversationSummary,
  FollowedThreadsPage,
  Mentionable,
  MentionsPage,
  MentionsRead,
  ThreadRead,
  UnreadSummary,
  ConversationView,
  Deleted,
  Edited,
  FileLink,
  HistoryPage,
  MessageCards,
  MessageView,
  ParticipantView,
  Pinned,
  PinView,
  Reacted,
  Sent,
  ThreadFollowing,
  ThreadPage,
  WholeChannelMentions,
} from "@beacon/core/chat"
import type {
  ConfirmedPublication,
  PublicationRequest,
  PublicationReview,
  Published,
} from "@beacon/core/chat-publishing"
import type { ChatSearchPage } from "@beacon/core/chat-search"
import type { SearchHit } from "@beacon/core/projects"
import { infiniteQueryOptions, queryOptions, type QueryClient } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import { Refused } from "@/administration/refused.ts"
import { apiBase } from "@/sign-in/client.ts"
import { forgetDrafts } from "./drafts.ts"
import { queryOf, searchPath, type SearchView } from "./search.ts"
import { threadTimelineOf, timelineOf } from "./timeline.ts"

/*
 * Chat's calls to the web API, and the queries the web app reads them through. What a Member may
 * read and do is the web API's to say; a refusal is thrown as `Refused`.
 */

/** Chat's place in the query cache, so a change can refresh all of it, or one conversation's. */
export const chatKeys = {
  all: ["chat"] as const,
  conversations: ["chat", "conversations"] as const,
  directory: ["chat", "directory"] as const,
  search: ["chat", "search"] as const,
  unreads: ["chat", "unreads"] as const,
  threads: ["chat", "threads"] as const,
  mentions: ["chat", "mentions"] as const,
  of: (id: string) => ["chat", "conversation", id] as const,
  /** The Member's saved messages: their ids, and the list showing each as it is now. */
  saved: ["chat", "saved"] as const,
}

/**
 * What is unread to the Member everywhere, for the badges: how many joined conversations and
 * followed threads hold something new. Kept current by live hints, and read again each minute
 * besides, as a stage without a socket has nothing else to say when.
 */
export const unreadsQuery = queryOptions({
  queryKey: chatKeys.unreads,
  queryFn: () => call<UnreadSummary>("GET", "/chat/unreads"),
  refetchInterval: 60_000,
})

/** The threads the Member follows, the latest replied to first, a page at a time. */
export const threadsQuery = infiniteQueryOptions({
  queryKey: chatKeys.threads,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }) =>
    call<FollowedThreadsPage>(
      "GET",
      `/chat/threads${pageParam === undefined ? "" : `?${new URLSearchParams({ cursor: pageParam }).toString()}`}`,
    ),
  getNextPageParam: (last) => last.nextCursor ?? undefined,
})

/** The messages that mentioned the Member, the latest first, a page at a time. */
export const mentionsQuery = infiniteQueryOptions({
  queryKey: chatKeys.mentions,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }) =>
    call<MentionsPage>(
      "GET",
      `/chat/mentions${pageParam === undefined ? "" : `?${new URLSearchParams({ cursor: pageParam }).toString()}`}`,
    ),
  getNextPageParam: (last) => last.nextCursor ?? undefined,
})

/**
 * Marks the Member's mentions in `messageIds` read, or every one of theirs when none are named,
 * for every device they use.
 */
export const markMentionsRead = (messageIds?: readonly string[]) =>
  call<MentionsRead>("PUT", "/chat/mentions/read", messageIds ? { messageIds } : {})

/**
 * Whom a message in the conversation may mention: its readers and every Group. Read again now and
 * then, as people join and leave; kept beside the conversation, so forgetting it forgets this.
 */
export const mentionableQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "mentionable"] as const,
    queryFn: () => call<Mentionable>("GET", `${path(id)}/mentionable`),
    staleTime: 60_000,
  })

/** Marks a joined conversation read up to the change `seq`, for every device the Member uses. */
export const markRead = (id: string, seq: number) =>
  call<ConversationRead>("PUT", `${path(id)}/read`, { seq })

/**
 * Marks every conversation in the Member's list with something unread read, up to its latest change
 * as the list holds it (what arrives after stays unread), then reads the list and what is unread
 * again. Answers how many it marked.
 */
export async function markAllRead(
  queryClient: QueryClient,
  conversations: readonly ConversationSummary[],
) {
  const unread = conversations.filter(({ unread }) => unread > 0)
  await Promise.all(unread.map(({ id, latestSeq }) => markRead(id, latestSeq)))
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
    queryClient.invalidateQueries({ queryKey: chatKeys.unreads }),
  ])
  return unread.length
}

/** Marks a followed thread read up to the change `seq`, for every device the Member uses. */
export const markThreadRead = (id: string, rootId: string, seq: number) =>
  call<ThreadRead>("PUT", `${threadPath(id, rootId)}/read`, { seq })

/**
 * How often an open conversation asks for what changed since it last asked while Chat's live socket
 * is down (or the stage has none). While it is up, hints say when to ask.
 */
export const catchUpEvery = 15_000

const path = (id: string) => `/chat/conversations/${encodeURIComponent(id)}`

/** The conversations the Member has joined, for the sidebar and the Chat page. */
export const conversationsQuery = queryOptions({
  queryKey: chatKeys.conversations,
  queryFn: async () =>
    (await call<{ conversations: ConversationSummary[] }>("GET", "/chat/conversations"))
      .conversations,
})

/** The public channels, a page at a time in name order, narrowed to names holding `query`. */
export const directoryQuery = (query: string) =>
  infiniteQueryOptions({
    queryKey: [...chatKeys.directory, query],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call<ChannelDirectoryPage>(
        "GET",
        `/chat/channels?${new URLSearchParams({
          ...(query && { query }),
          ...(pageParam !== undefined && { cursor: pageParam }),
        }).toString()}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

/**
 * A search of the messages the Member may read, a page at a time: the best matches first, or the
 * latest for filters alone. Never served from the cache without asking again, since access and
 * messages change; its Date filter is read as of when each page is asked for.
 */
export const searchQuery = (view: SearchView) =>
  infiniteQueryOptions({
    queryKey: [...chatKeys.search, view],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call<ChatSearchPage>("GET", searchPath(queryOf(view, new Date()), pageParam)),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 0,
  })

/** One conversation as the Member sees it: its name, whether they have joined it, who has. */
export const conversationQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "view"],
    queryFn: () => call<ConversationView>("GET", path(id)),
  })

/**
 * How long a message's Link cards are taken as current: after it, showing them again asks the web
 * API, so a grant lost, a ticket deleted or a Project archived shows within it, as returning to the
 * window does at once.
 */
export const linkCardsFreshFor = 30_000

/**
 * A message's Link cards, as the web API decides them for this Member now, for the message at its
 * `version`: an edit is another message to read them for, and a deletion leaves none to ask about.
 * Kept under its conversation, so forgetting the conversation forgets them.
 */
export const linkCardsQuery = (message: Pick<MessageView, "id" | "conversationId" | "version">) =>
  queryOptions({
    queryKey: [...chatKeys.of(message.conversationId), "cards", message.id, message.version],
    queryFn: () =>
      call<MessageCards>(
        "GET",
        `${path(message.conversationId)}/messages/${encodeURIComponent(message.id)}/cards`,
      ),
    staleTime: linkCardsFreshFor,
  })

/** Who has joined the conversation, in name order, and who of them manages it. */
export const participantsQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "participants"],
    queryFn: async () =>
      (await call<{ participants: ParticipantView[] }>("GET", `${path(id)}/participants`))
        .participants,
  })

/**
 * The messages pinned in the conversation, latest pinned first, each as it says now. Read again
 * whenever they or a pinned message change (`collectionsTouched`), and as the window comes back.
 */
export const pinsQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "pins"],
    queryFn: async () => (await call<{ pins: PinView[] }>("GET", `${path(id)}/pins`)).pins,
  })

/** Pins one of the conversation's messages for all its readers, or unpins it. */
export const pinMessage = (id: string, messageId: string, pinned: boolean) =>
  call<Pinned>(pinned ? "PUT" : "DELETE", `${path(id)}/pins/${encodeURIComponent(messageId)}`)

/**
 * A channel's bookmarks, in the order they were added, each link to Beacon's work with its Link
 * card as this Member may see it now: read again after `linkCardsFreshFor`, so a lost grant shows.
 */
export const bookmarksQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "bookmarks"],
    queryFn: async () =>
      (await call<{ bookmarks: BookmarkView[] }>("GET", `${path(id)}/bookmarks`)).bookmarks,
    staleTime: linkCardsFreshFor,
  })

const bookmarkPath = (id: string, bookmarkId: string) =>
  `${path(id)}/bookmarks/${encodeURIComponent(bookmarkId)}`

/** Adds a link to a channel's bookmarks; one it keeps already is answered as it is. */
export const addBookmark = (id: string, bookmark: { url: string; title: string }) =>
  call<Bookmarked>("POST", `${path(id)}/bookmarks`, bookmark)

/** Gives a bookmark another title; a blank one leaves the link to name itself. */
export const retitleBookmark = (id: string, bookmarkId: string, title: string) =>
  call<BookmarkView>("PATCH", bookmarkPath(id, bookmarkId), { title })

/** Takes a bookmark out of a channel. Taking it out again is the same. */
export const removeBookmark = (id: string, bookmarkId: string) =>
  call<null>("DELETE", bookmarkPath(id, bookmarkId))

/**
 * Drops everything the web app holds of a conversation the Member can no longer read (they left a
 * private one, were removed from it, or the web API now says it isn't theirs to see): its view,
 * timeline and participants, and its row in their own list, so no stale copy of it stays on show,
 * and whatever was being written in it, or kept unsent, on this device (`forgetDrafts`). What is on
 * screen empties at once; a query still on screen asks again, and is told it is gone.
 */
export function forgetConversation(queryClient: QueryClient, id: string) {
  forgetDrafts(id)
  void queryClient.resetQueries({ queryKey: chatKeys.of(id) })
  // A search's results may hold its messages: they are read again, as the Member may read them now.
  void queryClient.resetQueries({ queryKey: chatKeys.search })
  // Saved messages from it showed what it said: they are read again, as the web API decides now.
  void queryClient.resetQueries({ queryKey: [...chatKeys.saved, "list"] })
  queryClient.setQueryData<ConversationSummary[]>(chatKeys.conversations, (mine) =>
    mine?.filter((conversation) => conversation.id !== id),
  )
  // Nothing of it stays unread, nor among the threads the Member follows.
  void queryClient.invalidateQueries({ queryKey: chatKeys.unreads })
  void queryClient.invalidateQueries({ queryKey: chatKeys.threads })
  void queryClient.invalidateQueries({ queryKey: chatKeys.mentions })
}

/**
 * Reads a conversation again once its people, managers, archiving or details changed: its view and
 * who is in it, and its timeline only where reading it was refused (a Member added back to one
 * they were removed from, still on its page), as a timeline held is caught up from its cursor,
 * never read whole again.
 */
export function refreshConversation(queryClient: QueryClient, id: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: conversationQuery(id).queryKey }),
    queryClient.invalidateQueries({ queryKey: participantsQuery(id).queryKey }),
    // Whom it may mention follows who reads and joined it, and who may say @channel and @here.
    queryClient.invalidateQueries({ queryKey: mentionableQuery(id).queryKey }),
    queryClient.invalidateQueries({
      queryKey: timelineQuery(id).queryKey,
      predicate: (query) => query.state.status === "error",
    }),
  ])
}

/** A ticket that opens Chat's live socket for this session, for a minute, and where it is. */
export const fetchLiveTicket = () => call<{ url: string; ticket: string }>("POST", "/chat/live")

/** A page of history: the latest, or the one before `before`. */
export const fetchHistory = (id: string, before?: number) =>
  call<HistoryPage>("GET", `${path(id)}/messages${before === undefined ? "" : `?before=${before}`}`)

/** What changed in the conversation after the cursor `after`; with `thread`, in that thread. */
export const fetchChanges = (id: string, after: number, thread?: string) =>
  call<ChangesPage>(
    "GET",
    `${path(id)}/changes?${new URLSearchParams({
      after: String(after),
      ...(thread !== undefined && { thread }),
    }).toString()}`,
  )

const threadPath = (id: string, rootId: string) =>
  `${path(id)}/threads/${encodeURIComponent(rootId)}`

/** A page of the thread `rootId` started: the latest replies, or those before `before`. */
export const fetchThread = (id: string, rootId: string, before?: number) =>
  call<ThreadPage>(
    "GET",
    `${threadPath(id, rootId)}${before === undefined ? "" : `?before=${before}`}`,
  )

/**
 * A thread's timeline: read once from its latest page, then kept current as a conversation's is,
 * from its cursor (`useTimeline` with `thread`). It is the conversation's, so forgetting the
 * conversation forgets it too.
 */
export const threadQuery = (id: string, rootId: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "thread", rootId],
    queryFn: async () => threadTimelineOf(await fetchThread(id, rootId)),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  })

/** Follows the thread `rootId` started, or stops following it. */
export const followThread = (id: string, rootId: string, following: boolean) =>
  call<ThreadFollowing>(following ? "PUT" : "DELETE", `${threadPath(id, rootId)}/follow`)

/**
 * The conversation's timeline: read once from its latest page, then kept current by catching up
 * from its cursor (`useTimeline`), never read whole again while it is open.
 */
export const timelineQuery = (id: string) =>
  queryOptions({
    queryKey: [...chatKeys.of(id), "timeline"],
    queryFn: async () => timelineOf(await fetchHistory(id)),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  })

/**
 * Makes a channel, public or private for good; a retry carrying the same `operationId` answers the
 * same channel.
 */
export const createChannel = (name: string, operationId: string, visibility: ChannelVisibility) =>
  call<ConversationView>("POST", "/chat/channels", { name, operationId, visibility })

/**
 * Opens the direct message with `memberIds`: the one exactly those people have, or a new one. A
 * retry carrying the same `operationId` answers the same conversation.
 */
export const startDm = (memberIds: readonly string[], operationId: string) =>
  call<ConversationView>("POST", "/chat/dms", { memberIds, operationId })

export const joinConversation = (id: string) => call<ConversationView>("POST", `${path(id)}/join`)

/** Leaves it, answering it as the Member now sees it, or nothing once they can't. */
export const leaveConversation = (id: string) =>
  call<ConversationView | undefined>("POST", `${path(id)}/leave`)

/** Adds Members to a channel the Member manages, answering it as they now see it. */
export const addParticipants = (id: string, memberIds: readonly string[]) =>
  call<ConversationView>("POST", `${path(id)}/participants`, { memberIds })

/** Removes someone from a channel the Member manages; removing themselves is leaving. */
export const removeParticipant = (id: string, memberId: string) =>
  call<ConversationView | undefined>(
    "DELETE",
    `${path(id)}/participants/${encodeURIComponent(memberId)}`,
  )

/**
 * Makes a participant one of the channel's managers, or stops them being one (the Member themselves
 * stepping down), answering it as the Member now sees it.
 */
export const setManager = (id: string, memberId: string, manager: boolean) =>
  call<ConversationView>(
    manager ? "PUT" : "DELETE",
    `${path(id)}/managers/${encodeURIComponent(memberId)}`,
  )

/** Takes on managing a private channel the Member is in that has no active manager left. */
export const claimManagement = (id: string) => call<ConversationView>("POST", `${path(id)}/claim`)

/** Archives a channel the Member manages, or unarchives it. */
export const setArchived = (id: string, archived: boolean) =>
  call<ConversationView>("POST", `${path(id)}/${archived ? "archive" : "unarchive"}`)

/** Says what a channel the Member manages is for; a blank description clears it. */
export const describeChannel = (id: string, description: string) =>
  call<ConversationView>("PUT", `${path(id)}/description`, { description })

/** Links a channel the Member manages to the Project `key` names, or unlinks it (`null`). */
export const linkProject = (id: string, key: string | null) =>
  call<ConversationView>("PUT", `${path(id)}/project`, { key })

/** Says who may use `@channel` and `@here` in a channel the Member manages. */
export const setWholeChannelMentions = (id: string, who: WholeChannelMentions) =>
  call<ConversationView>("PUT", `${path(id)}/channel-mentions`, { who })

/**
 * Sends a message, carrying the files `fileIds` names (the Member's own, uploaded and confirmed),
 * or with `threadRootId` a reply in that message's thread; a retry carrying the same
 * `operationId` answers the first one.
 */
export const sendMessage = (
  id: string,
  body: string,
  operationId: string,
  { fileIds = [], threadRootId }: { fileIds?: readonly string[]; threadRootId?: string } = {},
) =>
  call<Sent>("POST", `${path(id)}/messages`, {
    body,
    operationId,
    ...(fileIds.length > 0 && { fileIds }),
    ...(threadRootId !== undefined && { threadRootId }),
  })

/**
 * Asks Beacon `body` as the operation `operationId` (sent as the Member's message, Beacon's reply
 * under it in its thread), in the thread `threadRootId` starts when given. A retry answers the first.
 */
export const askBeacon = (id: string, body: string, operationId: string, threadRootId?: string) =>
  call<Asked>("POST", `${path(id)}/beacon`, {
    body,
    operationId,
    ...(threadRootId !== undefined && { threadRootId }),
  })

/** Asks Beacon again for a reply it couldn't write, or that was withheld. */
export const askAgain = (id: string, messageId: string) =>
  call<MessageView>("POST", `${path(id)}/messages/${encodeURIComponent(messageId)}/ask-again`)

const filePath = (id: string, fileId: string) => `${path(id)}/files/${encodeURIComponent(fileId)}`

/** Declares a file about to be uploaded, answering the request that puts it in the store. */
export const startFile = (
  id: string,
  file: { fileName: string; contentType: string; size: number; md5: string },
) => call<ChatFileUpload>("POST", `${path(id)}/files`, file)

/** Checks the store holds the file as declared, answering it ready to send. */
export const confirmFile = (id: string, fileId: string) =>
  call<ChatFileView>("POST", `${filePath(id, fileId)}/confirm`)

/** Gives up a file the Member attached but hasn't sent. */
export const discardFile = (id: string, fileId: string) =>
  call<null>("DELETE", filePath(id, fileId))

/**
 * Where the browser fetches a file: the web API, which rechecks who may read it and redirects to a
 * link of a few minutes. `download` asks for it to be saved, an image too.
 */
export const fileHref = (id: string, fileId: string, { download = false } = {}) =>
  `${apiBase}${filePath(id, fileId)}${download ? "?download" : ""}`

/** A file's short-lived link itself, and when it lapses, for the page to fetch its bytes. */
export const fetchFileLink = (id: string, fileId: string) =>
  call<FileLink>("GET", `${filePath(id, fileId)}?link`)

const messagePath = (id: string, messageId: string) =>
  `${path(id)}/messages/${encodeURIComponent(messageId)}`

/**
 * Edits one of the Member's messages, based on the `version` they started from; a retry carrying
 * the same `operationId` answers the message as it is now.
 */
export const editMessage = (
  id: string,
  messageId: string,
  edit: { body: string; basedOn: number; operationId: string },
) => call<Edited>("PATCH", messagePath(id, messageId), edit)

/**
 * Sets the Member's reaction to a message with `emoji` (`reacted`), or takes it back, answering the
 * message as it is now. Sending either again is the same.
 */
export const setReaction = (id: string, messageId: string, emoji: string, reacted: boolean) =>
  call<Reacted>(
    reacted ? "PUT" : "DELETE",
    `${messagePath(id, messageId)}/reactions/${encodeURIComponent(emoji)}`,
  )

/** Deletes one of the Member's messages, answering its tombstone. Deleting it again is the same. */
export const deleteMessage = (id: string, messageId: string) =>
  call<Deleted>("DELETE", messagePath(id, messageId))

/**
 * What publishing excerpts would publish, where and to whom, as it stands now. Nothing keeps it:
 * not the web API, and not the query cache, so it is asked for afresh each time it is shown.
 */
export const reviewPublication = (request: PublicationRequest) =>
  call<PublicationReview>("POST", "/chat/publications/review", request)

/** Publishes the review `confirmed.reviewed` names, once per operation id. */
export const publishExcerpts = (confirmed: ConfirmedPublication) =>
  call<Published>("POST", "/chat/publications", confirmed)

/** The review as it is now, carried by a `review-changed` refusal; none from any other error. */
export function freshReviewOf(error: unknown): PublicationReview | undefined {
  if (!(error instanceof Refused) || error.refusal !== "review-changed") return undefined
  return (error.body as { review?: PublicationReview } | undefined)?.review
}

/**
 * The tickets the Member may add excerpts to now, matching `text` (words, or a ticket's key):
 * Stories and Bugs in Projects they can write to, best or most recently changed first.
 */
export const writableTicketsQuery = (text: string) =>
  queryOptions({
    queryKey: [...chatKeys.all, "publication-tickets", text],
    queryFn: () =>
      call<SearchHit[]>(
        "GET",
        `/chat/publications/tickets?${new URLSearchParams({ text }).toString()}`,
      ),
    staleTime: 30_000,
  })
